import axiosInstance from "../components/api/axiosInstance";

export const PARTY_TYPES = ["Organization", "Individual"];

/** Registration types for non-Indian parties (Indian parties use GSTIN / PAN). Must match the API. */
export const TAX_ID_TYPES = [
  { value: "EIN", label: "EIN (USA)" },
  { value: "VAT", label: "VAT number (UK / EU)" },
  { value: "ABN", label: "ABN (Australia)" },
  { value: "BN", label: "Business Number (Canada)" },
  { value: "TRN", label: "TRN (UAE)" },
  { value: "UEN", label: "UEN (Singapore)" },
  { value: "TIN", label: "TIN" },
  { value: "Other", label: "Other" }
];

/** Country name -> { taxType, dialCode } used to pre-fill the form when a country is picked. */
const COUNTRY_DEFAULTS = {
  india: { taxType: "", dialCode: "+91" },
  "united states": { taxType: "EIN", dialCode: "+1" },
  "united states of america": { taxType: "EIN", dialCode: "+1" },
  usa: { taxType: "EIN", dialCode: "+1" },
  "united kingdom": { taxType: "VAT", dialCode: "+44" },
  uk: { taxType: "VAT", dialCode: "+44" },
  canada: { taxType: "BN", dialCode: "+1" },
  australia: { taxType: "ABN", dialCode: "+61" },
  "united arab emirates": { taxType: "TRN", dialCode: "+971" },
  singapore: { taxType: "UEN", dialCode: "+65" }
};

export function isIndiaCountryName(name) {
  return String(name || "").trim().toLowerCase() === "india";
}

export function countryDefaults(name) {
  return COUNTRY_DEFAULTS[String(name || "").trim().toLowerCase()] || { taxType: "Other", dialCode: "" };
}

/**
 * Single printable registration line for a party or invoice snapshot, e.g. "GSTIN: 36ABC..." or
 * "EIN: 12-3456789". Returns [] when none.
 */
export function taxIdLines(p) {
  if (!p) return [];
  const out = [];
  if (p.GSTIN) out.push(`GSTIN: ${p.GSTIN}`);
  if (p.PAN) out.push(`PAN: ${p.PAN}`);
  if (p.Tax_Id) out.push(`${p.Tax_Id_Type || "Tax ID"}: ${p.Tax_Id}`);
  return out;
}

function normalizeList(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.items)) return raw.items;
  return [];
}

/**
 * GET bill-to parties. With clientId the tenant's own party and default come first
 * (flags Is_Self / Is_Default are relative to that tenant).
 * @param {{ clientId?: string|number, q?: string, status?: "Active"|"Inactive" }} [opts]
 */
export async function fetchBillToParties(opts = {}) {
  const params = {};
  if (opts.clientId != null && opts.clientId !== "") params.client_id = opts.clientId;
  if (opts.q) params.q = opts.q;
  if (opts.status) params.status = opts.status;
  const response = await axiosInstance.get("/fico/bill-to-parties", { params });
  return normalizeList(response.data);
}

export async function fetchBillToParty(billToId, clientId) {
  const params = clientId != null && clientId !== "" ? { client_id: clientId } : undefined;
  const response = await axiosInstance.get(`/fico/bill-to-parties/${billToId}`, { params });
  return response.data;
}

/**
 * POST a new party. Pass Set_Default_For_Client_Id to also make it that tenant's default.
 */
export async function createBillToParty(payload) {
  const response = await axiosInstance.post("/fico/bill-to-parties", payload);
  return response.data;
}

export async function updateBillToParty(billToId, payload) {
  const response = await axiosInstance.put(`/fico/bill-to-parties/${billToId}`, payload);
  return response.data;
}

/** Soft delete; invoices keep their snapshot. A tenant's own party cannot be deactivated. */
export async function deactivateBillToParty(billToId) {
  const response = await axiosInstance.delete(`/fico/bill-to-parties/${billToId}`);
  return response.data;
}

export function billToLabel(party) {
  if (!party) return "";
  const name = party.Name || "—";
  const tags = [];
  if (party.Is_Self) tags.push("own company");
  if (party.GSTIN) tags.push(party.GSTIN);
  else if (party.Tax_Id) tags.push(`${party.Tax_Id_Type || "Tax ID"} ${party.Tax_Id}`);
  return tags.length ? `${name} (${tags.join(", ")})` : name;
}

/** Shape the picker/modal and PDF helpers share: { name, contact, email, phone, address, gstin, pan, taxIdType, taxId }. */
export function billToDisplay(party) {
  if (!party) return null;
  return {
    name: party.Name || "",
    type: party.Party_Type || "",
    contact: party.Contact_Person || "",
    email: party.Email || "",
    phone: party.Phone ? [party.Country_Code, party.Phone].filter(Boolean).join(" ") : "",
    address: party.Address || "",
    gstin: party.GSTIN || "",
    pan: party.PAN || "",
    taxIdType: party.Tax_Id_Type || "",
    taxId: party.Tax_Id || ""
  };
}
